import axios from "axios";
import config from "../config/config";
import store from '../store';
import Swal from 'sweetalert2';

const API_URL = config.API_URL;
const AUTH_CACHE_TTL_MS = 60 * 1000;
let lastVerifiedAt = Number(sessionStorage.getItem('gt_auth_verified_at') || 0);
let lastVerifiedToken = sessionStorage.getItem('gt_auth_verified_token') || null;

function setVerifiedCache(token) {
    lastVerifiedAt = Date.now();
    lastVerifiedToken = token || '';
    try {
        sessionStorage.setItem('gt_auth_verified_at', String(lastVerifiedAt));
        sessionStorage.setItem('gt_auth_verified_token', lastVerifiedToken);
    } catch (e) {
        // Ignore sessionStorage errors
    }
}

function clearVerifiedCache() {
    lastVerifiedAt = 0;
    lastVerifiedToken = null;
    try {
        sessionStorage.removeItem('gt_auth_verified_at');
        sessionStorage.removeItem('gt_auth_verified_token');
    } catch (e) {
        // Ignore sessionStorage errors
    }
}

export function isSuperadminOnlyPath(path) {
    if (!path) return false;
    const p = path.toLowerCase();
    if (
        p === '/superadmin' || p.startsWith('/superadmin/') ||
        p.startsWith('/audit-logs') || p.startsWith('/audit-log') ||
        p.startsWith('/inbound-leads') || p.startsWith('/leads') ||
        p.startsWith('/game-benchmarks') ||
        p.startsWith('/creators') || p.startsWith('/creator-ecosystem') ||
        p === '/package' || p.startsWith('/package/') || p === '/packages' || p.startsWith('/packages/') ||
        p === '/discount' || p.startsWith('/discount/') || p === '/discounts' || p.startsWith('/discounts/') ||
        p === '/voucher' || p.startsWith('/voucher/') ||
        p === '/payment' || p.startsWith('/payment/') || p === '/payments' || p.startsWith('/payments/') ||
        p === '/quota' || p.startsWith('/quota/') || p.startsWith('/addquota') ||
        p.startsWith('/adminmaker') || p.startsWith('/admin-maker') ||
        p.startsWith('/adminplayer') || p.startsWith('/admin-player') ||
        p === '/owners' || p.startsWith('/owners/')
    ) {
        return true;
    }
    if ((p === '/owner' || p.startsWith('/owner/')) && !p.startsWith('/owner/analytics')) {
        return true;
    }
    return false;
}

export function isOwnerOnlyPath(path) {
    if (!path) return false;
    const p = path.toLowerCase();
    if (
        p.startsWith('/owner/analytics') ||
        p === '/analytics' || p === '/executive-analytics' ||
        p === '/billing' || p.startsWith('/billing/') ||
        p === '/pay' || p.startsWith('/pay/') ||
        p === '/makers' || p.startsWith('/makers/')
    ) {
        return true;
    }
    if ((p === '/maker' || p.startsWith('/maker/')) && !p.startsWith('/maker/studio')) {
        return true;
    }
    return false;
}

export function checkRoleRedirect(path, userData) {
    if (!userData) return null;
    const rawRole = userData.role || (userData.data && userData.data.role) || '';
    const userRoles = String(rawRole).split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
    const isSuperAdmin = userRoles.includes('superadmin');
    const isOwner = userRoles.includes('owner');

    if (isSuperadminOnlyPath(path) && !isSuperAdmin) {
        return '/dashboard';
    }
    if (isOwnerOnlyPath(path) && !isOwner && !isSuperAdmin) {
        return '/dashboard';
    }
    return null;
}

export default async function (to, from, next) {
    try {
        const token = store.state.authToken || localStorage.getItem('authToken') || localStorage.getItem('token');
        if (!token) {
            window.location.href = '/login';
            return;
        }

        let userData = store.state.user;
        const now = Date.now();
        const isCacheValid = userData && lastVerifiedToken === token && (now - lastVerifiedAt) < AUTH_CACHE_TTL_MS;

        if (!isCacheValid) {
            if (!userData) {
                const response = await axios.get(`${API_URL}auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                if (response.status !== 200 || !response.data?.data) {
                    clearVerifiedCache();
                    next('/login');
                    return;
                }

                userData = response.data.data;
                setVerifiedCache(token);
                store.dispatch('updateUser', userData);
            } else {
                setVerifiedCache(token);
                axios.get(`${API_URL}auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }).then((response) => {
                    if (store.state.authToken === token && response.status === 200 && response.data?.data) {
                        store.dispatch('updateUser', response.data.data);
                    }
                }).catch((err) => {
                    if (err && err.response && err.response.status === 401 && store.state.authToken === token) {
                        clearVerifiedCache();
                        if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
                            delete axios.defaults.headers.common['Authorization'];
                        }
                        store.commit('setAuthToken', null);
                        store.commit('setUser', null);
                        window.location.href = '/login';
                    }
                });
            }
        }

        const rawRole = userData.role || (userData.data && userData.data.role) || '';
        const userRoles = String(rawRole).split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
        const isPlayer = userRoles.includes('player');
        const hasAdminAccess = userRoles.includes('superadmin') || userRoles.includes('owner') || userRoles.includes('maker');

        if (isPlayer || !hasAdminAccess) {
            if (token && axios && typeof axios.post === 'function') {
                axios.post(`${API_URL}auth/logout`, null, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => {});
            }
            if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
                delete axios.defaults.headers.common['Authorization'];
            }
            clearVerifiedCache();
            localStorage.removeItem('tokenType');
            localStorage.removeItem('authToken');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            try {
                sessionStorage.clear();
            } catch (e) {
                // ignore
            }
            store.commit('setAuthToken', null);
            store.commit('setTokenType', null);
            store.commit('setUser', null);
            const isId = (localStorage.getItem('gamiteam_locale') || 'id') === 'id';
            Swal.fire({
                icon: 'error',
                title: isId ? 'Akses Ditolak' : 'Access Denied',
                text: isPlayer
                    ? (isId ? 'Anggota Tim harus menggunakan Portal Anggota Tim. Akses ke Portal Manajemen dibatasi.' : 'Team Members must use the Team Member Portal. Access to Management Portal is restricted.')
                    : (isId ? 'Anda tidak memiliki izin untuk mengakses Portal Manajemen.' : 'You do not have permission to access the Management Portal.'),
                confirmButtonColor: '#f59e0b',
            });
            next('/login');
            return;
        }

        const redirectTarget = checkRoleRedirect(to.path, userData);
        if (redirectTarget && to.path !== redirectTarget) {
            next(redirectTarget);
            return;
        }

        next();
    } catch (error) {
        const status = error && error.response ? error.response.status : null;
        if (status === 401 || !store.state.user) {
            clearVerifiedCache();
            if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
                delete axios.defaults.headers.common['Authorization'];
            }
            store.commit('setAuthToken', null);
            next('/login');
            return;
        }
        // For transient errors (e.g. 429 or network hiccup), fall back to cached user
        const cachedUser = store.state.user;
        const redirectTarget = checkRoleRedirect(to.path, cachedUser);
        if (redirectTarget && to.path !== redirectTarget) {
            next(redirectTarget);
            return;
        }
        next();
    }
}